package com.zq.webproject.ui.recharge

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.zq.webproject.network.Repository
import com.zq.webproject.utils.TokenManager
import com.zq.webproject.utils.UrlLauncher
import com.zq.webproject.getPlatform
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/**
 * 充值页面ViewModel
 * 管理充值功能和支付宝支付
 */
class RechargeViewModel {
    val repository = Repository()
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    // 加载状态
    var isLoading by mutableStateOf(false)
        private set

    // 错误信息
    var errorMessage by mutableStateOf<String?>(null)
        private set

    // 成功信息
    var successMessage by mutableStateOf<String?>(null)

    // 支付宝二维码URL
    var alipayQrcodeUrl by mutableStateOf<String?>(null)
        private set

    // 支付宝订单号（用于轮询）
    var alipayOrderNo by mutableStateOf<String?>(null)
        private set

    /**
     * 发起充值
     * @param amount 充值金额
     * @param onAlipayDialogShow 支付宝弹框显示回调
     */
    fun recharge(
        amount: Double,
        onAlipayDialogShow: ((String, String) -> Unit)? = null // 二维码URL, 订单号
    ) {
        scope.launch {
            val token = TokenManager.getToken()
            if (token.isNullOrEmpty()) {
                println("RechargeViewModel - Token为空，无法充值")
                errorMessage = "未登录，无法充值"
                return@launch
            }

            if (amount < 0.1) {
                println("RechargeViewModel - 充值金额小于0.1元")
                errorMessage = "充值金额不能小于0.1元"
                return@launch
            }

            println("RechargeViewModel - 开始充值，金额: $amount")
            isLoading = true
            errorMessage = null
            successMessage = null

            // 调用充值接口（支付宝支付）
            val result = repository.rechargeBalance(token, amount, "alipay")

            if (result.isFailure) {
                val error = "充值失败: ${result.exceptionOrNull()?.message}"
                println("RechargeViewModel - $error")
                errorMessage = error
                isLoading = false
                return@launch
            }

            val response = result.getOrNull()

            // 调试：打印完整响应
            println("RechargeViewModel - 完整响应: code=${response?.code}, message=${response?.message}, msg=${response?.msg}")
            println("RechargeViewModel - data内容: ${response?.data}")
            println("RechargeViewModel - img=${response?.img}, payurl=${response?.payurl}, orderNo=${response?.orderNo}")

            // 支付宝支付：检查响应中是否包含支付信息
            if (response != null) {
                // 支持两种响应格式
                val isSuccess = (response.code == 200) || (response.code == 1)
                // 优先使用 img 字段（第三方图片服务），直接显示不经过代理
                val qrcodeImageUrl = response.data?.img ?: response.img ?: response.data?.qrcode ?: response.qrcode
                val payurl = response.data?.payurl ?: response.payurl
                val orderNo = response.data?.orderNo ?: response.orderNo

                if (isSuccess && (qrcodeImageUrl != null || payurl != null)) {
                    println("RechargeViewModel - 充值订单创建成功")
                    println("RechargeViewModel - 支付链接: $payurl")
                    println("RechargeViewModel - 二维码图片: $qrcodeImageUrl")
                    println("RechargeViewModel - 支付宝订单号: $orderNo")

                    // 保存订单号用于轮询
                    alipayOrderNo = orderNo

                    // 判断平台
                    val platform = getPlatform().name
                    println("RechargeViewModel - 当前平台: $platform")
                    println("RechargeViewModel - 是否Android: ${platform.contains("Android", ignoreCase = true)}")
                    println("RechargeViewModel - 是否iOS: ${platform.contains("iOS", ignoreCase = true)}")
                    println("RechargeViewModel - payurl是否存在: ${payurl != null}")
                    println("RechargeViewModel - qrcodeImageUrl是否存在: ${qrcodeImageUrl != null}")
                                    
                    if ((platform.contains("Android", ignoreCase = true) ||
                                platform.contains("iOS", ignoreCase = true)) && payurl != null
                    ) {
                        // Android/iOS平台：直接调起支付宝APP
                        println("RechargeViewModel - ${platform}平台，直接打开支付宝APP: $payurl")
                        UrlLauncher.openUrl(payurl)
                        errorMessage = "已打开支付宝APP，支付完成后请返回查看状态"

                        // 启动后台轮询检查支付状态
                        if (orderNo != null) {
                            startPaymentStatusPolling(orderNo, token)
                        }
                    } else if (qrcodeImageUrl != null) {
                        // Web平台：通过代理接口加载第三方图片（解决CORS问题）
                        println("RechargeViewModel - Web平台，显示二维码弹框")
                        println("RechargeViewModel - 原始二维码URL: $qrcodeImageUrl")

                        // Web平台使用代理接口：/api/payment/proxy-image?url=xxx
                        val proxyImageUrl = if (platform.contains("Web", ignoreCase = true)) {
                            // 拼接代理URL
                            val proxyUrl = "https://shop.aiotzq.com/api/payment/proxy-image?url=$qrcodeImageUrl"
                            println("RechargeViewModel - 代理URL: $proxyUrl")
                            proxyUrl
                        } else {
                            // 非Web平台直接使用原始 URL
                            qrcodeImageUrl
                        }

                        alipayQrcodeUrl = proxyImageUrl

                        // 调用回调显示弹框
                        onAlipayDialogShow?.invoke(proxyImageUrl, orderNo ?: "")
                    } else {
                        errorMessage = "未获取到支付信息，请稍后重试"
                    }
                } else {
                    val errorMsg = response.message ?: response.msg ?: "充值失败"
                    errorMessage = errorMsg
                    println("RechargeViewModel - 充值失败: $errorMsg")
                }
            } else {
                errorMessage = "充值响应为空"
            }

            isLoading = false
        }
    }

    /**
     * 启动支付状态轮询
     */
    private fun startPaymentStatusPolling(orderNo: String, token: String) {
        scope.launch {
            try {
                println("RechargeViewModel - 启动支付状态轮询，订单号: $orderNo")
                // 等待5秒后开始检查
                delay(5000)

                var retryCount = 0
                val maxRetries = 10
                var paymentVerified = false

                while (retryCount < maxRetries && !paymentVerified) {
                    if (retryCount > 0) {
                        println("RechargeViewModel - 第${retryCount + 1}次查询支付状态，等待3秒...")
                        delay(3000)
                    }

                    println("RechargeViewModel - 检查支付状态，订单号: $orderNo (第${retryCount + 1}次)")
                    val checkResponse = repository.getOrderDetail(orderNo, token)

                    if (checkResponse.isSuccess) {
                        val checkOrderDetail = checkResponse.getOrNull()
                        if (checkOrderDetail?.code == 200 && checkOrderDetail.data != null) {
                            val payStatus = checkOrderDetail.data.payStatus
                            println("RechargeViewModel - 当前支付状态: $payStatus (0=未支付, 1=已支付)")

                            if (payStatus == 1) {
                                println("RechargeViewModel - 🎉 支付成功！充值完成")
                                paymentVerified = true
                                errorMessage = null
                                successMessage = "充值成功！"
                            } else {
                                retryCount++
                            }
                        } else {
                            println("RechargeViewModel - 查询订单失败: ${checkOrderDetail?.message}")
                            retryCount++
                        }
                    } else {
                        println("RechargeViewModel - 网络请求失败: ${checkResponse.exceptionOrNull()?.message}")
                        retryCount++
                    }
                }

                if (!paymentVerified) {
                    println("RechargeViewModel - 未检测到支付，请手动刷新")
                    errorMessage = "未检测到支付，如已支付请返回我的页面查看余额"
                }
            } catch (e: Exception) {
                println("RechargeViewModel - 支付状态检查异常: ${e.message}")
                e.printStackTrace()
            }
        }
    }

    /**
     * 清除错误信息
     */
    fun clearError() {
        errorMessage = null
    }

    /**
     * 清除成功信息
     */
    fun clearSuccess() {
        successMessage = null
    }
}
